import React from 'react';
import { ComboboxProps, BoxProps } from 'pounceV2';
import { useField, FieldConfig } from 'formik';
import useEventHandler from 'Hooks/useEventHandler';
import CompoundInputV3 from 'Components/CompoundInput/CompoundInputV3';
import { FieldFilterOperators, FieldFilterTypes } from '../constants';

type FieldFilterProp = {
  fieldType: string;
  currentOperator?: string;
} & Partial<Omit<ComboboxProps<string>, 'onChange' | 'items' | 'value'>> &
  Pick<FieldConfig, 'name' | 'value' | 'innerRef' | 'validate'> &
  BoxProps;

const FilterOperatorComboboxV3 = React.forwardRef<HTMLInputElement, FieldFilterProp>(
  function FilterOperatorCombobox(props: FieldFilterProp, ref): React.ReactElement {
    const { fieldType, disabled, ...fieldProps } = props;
    // @ts-ignore
    const [field, , { setValue }] = useField(fieldProps as FieldConfig);
    // Combos & Downshift do not use standard onChange in the manner that Formik expects
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { onChange, ...rest } = field;
    const setFieldValue = useEventHandler(setValue);
    const normalizedFieldType = fieldType ? FieldFilterTypes[fieldType.toUpperCase()] : null;
    const items = React.useMemo(
      () =>
        (normalizedFieldType
          ? FieldFilterOperators.filter(({ fieldTypes }) =>
              fieldTypes.includes(normalizedFieldType),
            )
          : FieldFilterOperators
        ).map(({ value }) => value),
      [normalizedFieldType],
    );

    return (
      <CompoundInputV3.AutoCompleteInput
        disabled={disabled}
        items={items}
        itemToString={op => FieldFilterOperators.find(({ value }) => op === value)?.label}
        data-tid="no-code-filter-operator"
        label="Operator"
        placeholder="Select an operator"
        onSelect={f => setFieldValue(f, false)}
        fuzzySearch
        {...fieldProps}
        {...rest}
        ref={ref}
      />
    );
  },
);

export default FilterOperatorComboboxV3;
